import { observable, action, toJS } from 'mobx';
import api from '~/api';
import accountStore from './accountStore';

const WS_KEY = 'debug:websocket';

class Ig {
  @observable events = [];
  @observable statsByTeam = {};
  @observable statsByTeamTexts = [];
  @observable notExists = true;
  @observable game = null;
  @observable error = false;

  @observable video = {
    gid: null,
    src: null,
    playing: true,
    volume: 0.5,
    title: null,
    detached: false,
    error: null,
    type: null,
    isLoading: false,
    started: false,
    ready: false,
    showList: false,
  };

  attempts = 0;

  latestSubData = null;

  ws = null;

  lang = {
    1: 1049, // ru
    5: 1065, // fa
    2: 2057, // en
  };

  constructor(store) {
    // this.store = store;
  }

  init(id, lang_id) {
    if ('WebSocket' in window) {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        const ws = this.getSocket();
        ws.onopen = () => this.subscribe(id, lang_id);
        this.latestSubData = { id, lang_id };
      } else {
        this.subscribe(id, lang_id);
      }
    }
  }

  get url() {
    // ! Proxy
    // return 'ws://pre.almara.org:8084/wss';
    return (
      localStorage.getItem(WS_KEY) ||
      `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/wss`
    );
  }

  getSocket = () => {
    if (!('WebSocket' in window)) { return; }
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) { return this.ws; }
    this.ws = new WebSocket(this.url);
    this.ws.addEventListener('message', this.onMessage);
    this.ws.addEventListener('close', this.onClose);
    this.ws.addEventListener('error', this.onError);
    return this.ws;
  }

  send = data => {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  };

  subscribe = (id, lang_id) => {
    this.attempts = 0;
    const data = {
      t: 4,
      op: 0,
      d: {
        id,
        lng: this.lang[lang_id],
      },
    };
    this.latestSubData = { id, lang_id };
    this.send(data);
  };

  unSubscribe = () => {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({
        t: 4,
        op: 1,
      });
    }
    this.reset();
  };

  @action setData(data) {
    if (data.d.notExists) {
      this.game.stats = 0;
      this.reset();
      return false;
    }
    if (data.d.diff === false) {
      this.reset();
    }
    this.notExists = false;
    if (data.d.score) {
      this.game.score = data.d.score;
    }
    if (data.d.events) {
      this.events.unshift(...data.d.events);
    }
    this.statsByTeam = { ...this.statsByTeam, ...data.d.statsByTeam };
    if (data.d.statsByTeamTexts) {
      this.statsByTeamTexts = data.d.statsByTeamTexts;
    }
  }

  onMessage = (e) => {
    const data = JSON.parse(e.data);
    if (data.t === 28) {
      accountStore.updateBets(data.d);
    }
    if(data.t === 14) {
      accountStore.updateCountBets(data.d);
    }
    if (data.t === 13) {
      accountStore.updateBalance(data.d);
    }
    if (data.t !== 4) { return; }
    this.setData(data);
  };

  onClose = evt => {
    this.ws = null;
    console.log(evt);
    if (evt.code === 1001) {
      // Going away
      this.reInit();
    }
  };

  @action onError = () => {
    if (this.attempts < 1) {
      this.attempts++;
      setTimeout(() => {
        this.reInit();
      }, 500);
    } else {
      this.attempts = 0;
      this.error = true;
    }
  };

  @action reInit = () => {
    this.error = false;
    if (this.latestSubData) {
      this.init(this.latestSubData.id, this.latestSubData.lang_id);
    }
  };

  handleLogout() {
    if (this.video.type === 2) {
      // медиасервер
      this.setVideoParam('src', null);
    }
  }

  @action setVideoParam = (name, val) => {
    const video = {
      ...toJS(this.video),
      [name]: val,
    };
    if (name === 'src' && !val) {
      video.gid = null;
    }
    this.video = video;
  };

  @action getVideo({ gid, type, silent, retry }) {
    this.video.detached = false;
    if (!retry) {
      this.video.src = null;
    }
    this.video.title = null;
    this.video.isLoading = true;
    this.video.ready = false;
    this.video.started = false;
    this.video.isLoading = false;
    api.live.video(gid, type).then(res => this.onLoadVideo({ gid, silent, res }));
  }

  @action onLoadVideo = ({ gid, silent, res }) => {
    if (res.code === 0) {
      this.video = {
        ...toJS(this.video),
        playing: !silent,
        gid,
        src: res.result[gid].url,
        title: res.result[gid].name,
        type: res.result[gid].type,
        detached: false,
        error: null,
        showList: false,
      };
    } else if (res.code === 1) {
      this.video.error = res.text || 'SERVICE_TEMPORARILY_DOWN';
    } else {
      this.video.error = res.code;
    }
  };

  @action setGame(game) {
    if (!((game && game.video_type) || game.stats)) return;
    if (this.game) {
      this.close();
    }
    this.game = toJS(game);
    const { id, video_type } = game;
    const type = video_type && video_type[0];
    if (type) {
      this.getVideo({ gid: id, type, silent: true });
    }
    if (game.stats) {
      this.init(game.id, 1);
    }
  }

  @action close() {
    this.unSubscribe();
    this.game = null;
    this.video = {
      gid: null,
      src: null,
      playing: true,
      volume: 0.5,
      title: null,
      detached: false,
      error: null,
      type: null,
      isLoading: false,
      started: false,
      ready: false,
      showList: false,
    };
  }

  @action reset() {
    this.events = [];
    this.statsByTeam = {};
    this.statsByTeamTexts = [];
    this.notExists = true;
    this.error = false;
  }
}

export default Ig;



// WEBPACK FOOTER //
// ./src/stores/ig.js